import {
  ALWAYS_VIEW_PAGE_KEYS,
  isPageLicensed,
  getEffectivePermissionsFromMap,
} from "@repo/permissions";

/**
 * Merge roleAuthorization from multiple roles: union of permissions per page (OR).
 * @param {Array<{ roleAuthorization?: Array<{ page: string, permissions: string[] }> }>} roles
 */
export function buildPermissionMapFromRoles(roles) {
  const map = {};
  if (!Array.isArray(roles)) return map;
  for (const role of roles) {
    const auth = role?.roleAuthorization;
    if (!Array.isArray(auth)) continue;
    for (const item of auth) {
      if (!item?.page || !Array.isArray(item.permissions)) continue;
      const prev = map[item.page] || [];
      map[item.page] = [...new Set([...prev, ...item.permissions])];
    }
  }
  return map;
}

/**
 * Resolve active tenant membership for permission checks (same rules as usePermission).
 * @param {object} user
 * @param {string|null|undefined} selectedTenantId
 */
export function resolveTenantMembership(user, selectedTenantId) {
  const memberships = user?.memberships;
  if (!Array.isArray(memberships) || !selectedTenantId) return null;
  return (
    memberships.find((m) => {
      const tId = m?.tenant?.id || m?.tenant?._id || m?.tenant;
      return String(tId) === String(selectedTenantId);
    }) ?? null
  );
}

/**
 * Build permission map for the active tenant (supports multi-role and legacy single role).
 */
export function buildTenantPermissionMap(membership, user) {
  const tenantRoles = membership?.roles;
  if (Array.isArray(tenantRoles) && tenantRoles.length > 0) {
    return buildPermissionMapFromRoles(tenantRoles);
  }
  const singleAuth = membership?.role?.roleAuthorization;
  if (Array.isArray(singleAuth)) {
    return buildPermissionMapFromRoles([{ roleAuthorization: singleAuth }]);
  }
  const legacy = user?.role?.roleAuthorization;
  if (Array.isArray(legacy)) {
    return buildPermissionMapFromRoles([{ roleAuthorization: legacy }]);
  }
  return {};
}

/** Effective permissions for a page (shared with API via @repo/permissions). */
export function getEffectivePermissionsForPage(permissionMap, pageKey) {
  return getEffectivePermissionsFromMap(permissionMap, pageKey);
}

/**
 * Snapshot for non-React callers (e.g. tenant refetch registry).
 */
export function getTenantRolePermissionSnapshot(user, selectedTenantId) {
  const membership = resolveTenantMembership(user, selectedTenantId);
  const licensedModules = membership?.tenant?.settings?.modules || [];
  const permissionMap = buildTenantPermissionMap(membership, user);
  return { membership, licensedModules, permissionMap };
}

/**
 * Same semantics as usePermission().hasAny(pageKey): licensed module + at least one action on page.
 */
export function tenantSnapshotHasAnyPagePermission(snapshot, pageKey) {
  if (!pageKey) return false;
  const { licensedModules, permissionMap } = snapshot;
  if (!isPageLicensed(pageKey, licensedModules)) return false;
  const perms = getEffectivePermissionsForPage(permissionMap, pageKey);
  return Array.isArray(perms) && perms.length > 0;
}

/**
 * True if user has hasAny on at least one page key (OR). Empty list returns true (no gate).
 */
export function tenantSnapshotHasAnyPagePermissionOr(snapshot, pageKeys) {
  if (!Array.isArray(pageKeys) || pageKeys.length === 0) return true;
  return pageKeys.some((k) => tenantSnapshotHasAnyPagePermission(snapshot, k));
}

/**
 * True if the tenant role grants at least one action on at least one licensed page.
 * Used to skip heavy reference-data hydration for sessions with no effective UI access.
 * Core shell pages (home / myWork) always count as usable when licensed.
 */
export function tenantSnapshotHasAnyUsablePermission(snapshot) {
  const { licensedModules, permissionMap } = snapshot;
  for (const pageKey of ALWAYS_VIEW_PAGE_KEYS) {
    if (isPageLicensed(pageKey, licensedModules)) return true;
  }
  for (const [pageKey, perms] of Object.entries(permissionMap || {})) {
    if (!Array.isArray(perms) || perms.length === 0) continue;
    if (isPageLicensed(pageKey, licensedModules)) return true;
  }
  return false;
}
